import { IonImg } from '@ionic/react';
import clsx from 'clsx';
import React from 'react';
import { useSelector } from 'react-redux';
import { IState } from 'appRedux/createStore';
import {
  ChallengeStyleData,
  EventCardSubtitle,
  buildChallengeAssetUrl,
  resolveCompleteImageUrl,
  resolveDescription,
} from 'pages/EventsTab/EventCard';

export interface DetailEventCardProps {
  title: React.ReactNode;
  subtitle?: React.ReactNode;
  ctaLabel?: string;
  onCtaClick?: () => void;
  onExploreClick?: () => void;
  onCardClick?: () => void;
  imageSrc?: string;
  imageAlt?: string;
  backslash?: boolean;
  className?: string;
  joined?: boolean;
  casinoLogoSrc?: string;
  styleData?: ChallengeStyleData;
  participantStatus?: string;
  startDate?: string | Date;
  endDate?: string | Date;
  viewableDate?: string | Date;
  descriptions?: { pending?: string; enrolled?: string; running?: string; complete?: string };
  justJoined?: boolean;
}

const formatStartDate = (date: Date): string => {
  const month = date.toLocaleDateString('en-US', { month: 'long' });
  const day = date.getDate();
  const suffix =
    day % 10 === 1 && day !== 11 ? 'st'
    : day % 10 === 2 && day !== 12 ? 'nd'
    : day % 10 === 3 && day !== 13 ? 'rd'
    : 'th';
  return `Starting ${month} ${day}${suffix}`;
};

/**
 * CTA label priority:
 * 1. Event completed (now > endDate)        → "Results"
 * 2. Event not started (now < startDate)    → "Starting [Month Day]"
 * 3. Running + enrolled                     → "Play Now"
 * 4. Running + pending                      → "Join Now"
 * 5. Default                               → ctaLabel
 */
const getDetailCtaLabel = (
  participantStatus?: string,
  startDate?: string | Date,
  endDate?: string | Date,
  defaultCtaLabel?: string
): string | undefined => {
  const normalizedStatus = participantStatus?.toString().trim().toLowerCase();
  const now = new Date();

  if (endDate && now > new Date(endDate)) {
    return defaultCtaLabel;
  }

  if (startDate && now < new Date(startDate)) {
    return formatStartDate(new Date(startDate));
  }

  if (normalizedStatus === 'enrolled') {
    return 'Play Now';
  }

  if (normalizedStatus === 'pending') {
    return 'Join Now!';
  }

  return defaultCtaLabel;
};

const DetailEventCard: React.FC<DetailEventCardProps> = ({
  title,
  subtitle,
  ctaLabel,
  onCtaClick,
  onExploreClick,
  onCardClick,
  imageSrc,
  imageAlt = 'Event',
  backslash = true,
  className,
  joined = false,
  casinoLogoSrc,
  styleData,
  participantStatus,
  startDate,
  endDate,
  viewableDate,
  descriptions,
  justJoined,
}) => {
  const isCasinoAccount = useSelector<IState>(
    (state) => state.app.persistedState.userInfo?.isCasinoAccount
  ) as boolean;
  const isSlotcheckInternal = useSelector<IState>((state) =>
    state.app.persistedState.userInfo?.groups?.includes('SlotcheckInternal')
  );
  const resolvedSubtitle = resolveDescription(
    typeof subtitle === 'string' ? subtitle : undefined,
    descriptions,
    participantStatus,
    endDate,
    justJoined
  ) ?? subtitle;

  const derivedCharImage = buildChallengeAssetUrl(styleData?.charimg);
  const derivedBackgroundImage = buildChallengeAssetUrl(styleData?.background);
  const resolvedImageSrc = derivedCharImage || imageSrc;

  const resolvedBackgroundImage = derivedBackgroundImage;
  const cardStyle: React.CSSProperties | undefined =
    styleData?.backgroundColor || styleData?.primaryColor || resolvedBackgroundImage
      ? {
          backgroundColor: styleData?.backgroundColor || styleData?.primaryColor || undefined,
          ...(resolvedBackgroundImage
            ? { backgroundImage: `url(${resolvedBackgroundImage})` }
            : {}),
        }
      : undefined;

  const completeImageUrl = resolveCompleteImageUrl(styleData, endDate, viewableDate);
  const showJoinedLogo = !!completeImageUrl;
  const effectiveCtaLabel = getDetailCtaLabel(participantStatus, startDate, endDate, ctaLabel);
  return (
    <div
      className={clsx(
        'event-card',
        'event-card--feature',
        backslash ? 'event-card--backslash' : null,
        className
      )}
      style={cardStyle}
      onClick={onCardClick}
    >
      <div className="event-card__content" style={styleData?.alignTop ? { alignSelf: 'stretch', paddingBottom: '14px' } : undefined}>
        <h2 className="event-card__title" style={{ color: styleData?.textColor }}>
          {title}
        </h2>
        <EventCardSubtitle subtitle={resolvedSubtitle} color={styleData?.secondaryColor} />

        {!effectiveCtaLabel && (showJoinedLogo || joined) && (
          <div className="event-card__joined" style={{ visibility: joined ? 'visible' : 'hidden', ...(styleData?.alignTop ? { marginTop: 'auto' } : {}) }}>
            {casinoLogoSrc ? (
              <img src={casinoLogoSrc} alt="Casino" style={{ height: 36, marginRight: 12 }} />
            ) : null}
            {casinoLogoSrc ? (
              <span aria-hidden="true" className="event-card__joined-divider" />
            ) : null}
            <img
              src="assets/images/SlotCheck-White.png"
              alt="SlotCheck"
              style={{ width: 58, height: 40, objectFit: 'contain' }}
            />
          </div>
        )}

        {effectiveCtaLabel && !isCasinoAccount && !isSlotcheckInternal && (
          <button
            className="event-card__cta"
            onClick={(e) => {
              e.stopPropagation();
              onCtaClick && onCtaClick();
            }}
            style={{
              background: styleData?.btnJoinBackground || styleData?.ctaBackground || styleData?.btncolor || undefined,
              color: styleData?.btnJoinColor || styleData?.textColor,
              ...(styleData?.alignTop ? { marginTop: 'auto' } : {}),
            }}
          >
            <span className="event-card__play-icon" />
            {effectiveCtaLabel}
          </button>
        )}
      </div>

      {!completeImageUrl && <button
        className="event-card__cta event-card__ctaexplore"
        onClick={(e) => {
          e.stopPropagation();
          onExploreClick && onExploreClick();
        }}
        style={{
          background: styleData?.ctaBackground || 'rgba(0, 0, 0, 0.25)',
          color: styleData?.textColor,
        }}
      >
        <svg
          className="event-card__explore-icon"
          xmlns="http://www.w3.org/2000/svg"
          width="24"
          height="24"
          viewBox="0 0 24 24"
          fill="none"
          aria-hidden="true"
        >
          <path
            fillRule="evenodd"
            clipRule="evenodd"
            d="M12.1959 3.99994C16.7194 3.99994 20.3918 7.67239 20.3918 12.1959C20.3918 16.7194 16.7194 20.3918 12.1959 20.3918C7.67239 20.3918 3.99994 16.7194 3.99994 12.1959C3.99994 7.67239 7.67239 3.99994 12.1959 3.99994ZM12.623 19.5254C16.3352 19.3121 19.3121 16.3352 19.5254 12.623H18.3713C18.1356 12.623 17.9442 12.4316 17.9442 12.1959C17.9442 11.9602 18.1356 11.7688 18.3713 11.7688H19.5254C19.3121 8.05652 16.3352 5.07967 12.623 4.86631V6.02046C12.623 6.25617 12.4316 6.44756 12.1959 6.44756C11.9602 6.44756 11.7688 6.25617 11.7688 6.02046V4.86631C8.05652 5.07967 5.07967 8.05652 4.86631 11.7688H6.02046C6.25617 11.7688 6.44756 11.9602 6.44756 12.1959C6.44756 12.4316 6.25617 12.623 6.02046 12.623H4.86631C5.07967 16.3352 8.05652 19.3121 11.7688 19.5254V18.3713C11.7688 18.1356 11.9602 17.9442 12.1959 17.9442C12.4316 17.9442 12.623 18.1356 12.623 18.3713V19.5254Z"
            fill="white"
          />
          <path
            fillRule="evenodd"
            clipRule="evenodd"
            d="M14.7595 15.0703L8.19949 16.7103C8.05398 16.7466 7.90002 16.704 7.79391 16.5979C7.68785 16.4918 7.64521 16.3379 7.68157 16.1924L9.32157 9.63235C9.32391 9.62305 9.32654 9.61386 9.32949 9.60476L9.3324 9.59599C9.35729 9.52424 9.4005 9.46139 9.45655 9.41288C9.50718 9.36922 9.56749 9.33789 9.63235 9.32157L16.1924 7.68157C16.3379 7.64521 16.4918 7.68785 16.5979 7.79391C16.704 7.90002 16.7466 8.05398 16.7103 8.19949L15.0702 14.7595C15.056 14.8163 15.0301 14.8698 14.9945 14.9163C14.9474 14.9775 14.8841 15.0254 14.8108 15.0539L14.8027 15.057C14.7886 15.0621 14.7742 15.0666 14.7595 15.0703ZM10.0363 10.2954L8.68288 15.709L14.0809 14.3595C13.2511 12.385 11.9103 11.0218 10.0363 10.2954Z"
            fill="white"
          />
        </svg>
        Explore
      </button>}
      {/* {showJoinedLogo && (
        <div className="event-card__joined" style={{ position: 'absolute', bottom: 5, left: 15 }} aria-hidden>
          {casinoLogoSrc ? (
            <img src={casinoLogoSrc} alt="Casino" style={{ height: 36, marginRight: 12 }} />
          ) : null}
          {casinoLogoSrc ? (
            <span aria-hidden="true" className="event-card__joined-divider" />
          ) : null}
          <img
            src="assets/images/SlotCheck-White.png"
            alt="SlotCheck"
            style={{ width: 58, height: 40, objectFit: 'contain' }}
          />
        </div>
      )} */}
      {resolvedImageSrc && (
        <div className="event-card__image">
          <IonImg src={resolvedImageSrc} alt={imageAlt} />
        </div>
      )}
      {completeImageUrl && (
        <div
          className="event-card__complete-overlay"
          style={{ position: 'absolute', bottom: 0, right: 0 ,zIndex:999}}
        >
          <IonImg src={completeImageUrl} alt="Completed" />
        </div>
      )}
    </div>
  );
};

export default DetailEventCard;
